import * as Sentry from '@sentry/react';
import { supabase } from './supabase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class ApiClient {
  private token: string | null = null;
  private tenantId: string | null = null;
  private refreshingToken: Promise<string | null> | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  setTenantId(tenantId: string | null) {
    this.tenantId = tenantId;
  }

  /**
   * Refresh token from Supabase if expired
   */
  private async refreshToken(): Promise<string | null> {
    // Prevent multiple simultaneous refresh calls
    if (this.refreshingToken) {
      return this.refreshingToken;
    }

    this.refreshingToken = (async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error || !data.session) {
          console.warn('Failed to refresh token:', error?.message);
          return null;
        }
        this.token = data.session.access_token;
        return this.token;
      } catch (err) {
        console.error('Token refresh error:', err);
        return null;
      } finally {
        this.refreshingToken = null;
      }
    })();

    return this.refreshingToken;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    retryOn401 = true,
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.tenantId) {
      headers['X-Tenant-ID'] = this.tenantId;
    }

    try {
      const response = await fetch(`${API_URL}/api/v1${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      // Handle 401 - try to refresh token and retry once
      if (response.status === 401 && retryOn401) {
        console.log('Got 401, attempting token refresh...');
        const newToken = await this.refreshToken();
        if (newToken) {
          // Retry the request with new token
          return this.request<T>(method, path, body, false);
        } else {
          // Refresh failed - sign out and redirect to login
          console.warn('Token refresh failed, signing out...');
          await supabase.auth.signOut();
          window.location.href = '/login';
          throw new ApiError('Session expired', 401);
        }
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const errorMessage = error.message || `HTTP ${response.status}`;
        
        // Report to Sentry for 5xx errors
        if (response.status >= 500) {
          Sentry.captureMessage(`API Error: ${errorMessage}`, {
            extra: { path, method, status: response.status, error },
          });
        }

        throw new ApiError(errorMessage, response.status, error);
      }

      // Handle empty responses
      const text = await response.text();
      return text ? JSON.parse(text) : ({} as T);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      Sentry.captureException(error);
      throw new ApiError('Network error', 0);
    }
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  async uploadFile<T>(path: string, formData: FormData): Promise<T> {
    const headers: Record<string, string> = {};

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.tenantId) {
      headers['X-Tenant-ID'] = this.tenantId;
    }

    try {
      const response = await fetch(`${API_URL}/api/v1${path}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const errorMessage = error.message || `HTTP ${response.status}`;
        
        if (response.status >= 500) {
          Sentry.captureMessage(`API Error: ${errorMessage}`, {
            extra: { path, method: 'POST', status: response.status, error },
          });
        }

        throw new ApiError(errorMessage, response.status, error);
      }

      const text = await response.text();
      return text ? JSON.parse(text) : ({} as T);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      Sentry.captureException(error);
      throw new ApiError('Network error', 0);
    }
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = new ApiClient();
